import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, CheckCircle, Smartphone, Globe, Monitor, Loader2, Code, Users, Laptop, Tv, Watch, Tablet } from 'lucide-react';
import Button from '@/components/ui/Button';
import { Platform, RevenueModel, ProjectDescription, SelectedFunctionality } from '@/types/estimation';
import { FUNCTIONALITIES, getRecommendedFunctionalities } from '@/lib/functionalities';

interface QuestionnaireStepsProps {
  onComplete: (data: {
    platforms: Platform[];
    revenueModels: RevenueModel[];
    projectDescription: ProjectDescription;
    selectedFunctionalities: SelectedFunctionality[];
  }) => void;
}

const QuestionnaireSteps: React.FC<QuestionnaireStepsProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    platforms: [] as Platform[],
    description: '',
    revenueModels: [] as RevenueModel[],
    targetUsers: '',
    selectedFunctionalities: [] as SelectedFunctionality[],
    techStack: [] as string[],
  });

  const techStackOptions = [
    'React', 'Vue.js', 'Angular', 'Next.js', 'Nuxt.js',
    'React Native', 'Flutter', 'Swift', 'Kotlin',
    'Node.js', 'Python', 'Django', 'Laravel', 'Ruby on Rails',
    'PostgreSQL', 'MongoDB', 'MySQL', 'Redis',
    'AWS', 'Google Cloud', 'Azure', 'Vercel', 'Firebase',
  ];

  const revenueModelOptions = [
    { value: 'subscription' as RevenueModel, label: 'Abonnement', description: 'Revenus récurrents mensuels/annuels' },
    { value: 'in-app-purchase' as RevenueModel, label: 'Achats in-app', description: 'Vente de produits/services dans l\'app' },
    { value: 'ads' as RevenueModel, label: 'Publicités', description: 'Revenus publicitaires' },
    { value: 'commission' as RevenueModel, label: 'Commission', description: 'Commission sur transactions' },
    { value: 'one-time-payment' as RevenueModel, label: 'Paiement unique', description: 'Achat unique du produit/service' },
    { value: 'freemium' as RevenueModel, label: 'Freemium', description: 'Gratuit avec version premium' },
    { value: 'sponsorship' as RevenueModel, label: 'Sponsoring', description: 'Contenu sponsorisé' },
    { value: 'other' as RevenueModel, label: 'Autre', description: 'Autre modèle de revenus' },
  ];

  const platformOptions = [
    { value: 'web' as Platform, label: 'Application Web', icon: Globe, description: 'Site web responsive' },
    { value: 'mobile' as Platform, label: 'Application Mobile', icon: Smartphone, description: 'iOS et Android' },
    { value: 'desktop' as Platform, label: 'Application Desktop', icon: Laptop, description: 'Windows, macOS, Linux' },
    { value: 'tablet' as Platform, label: 'Tablette', icon: Tablet, description: 'iPad, Android tablets' },
    { value: 'tv' as Platform, label: 'Smart TV', icon: Tv, description: 'Apple TV, Android TV, etc.' },
    { value: 'watch' as Platform, label: 'Montre connectée', icon: Watch, description: 'Apple Watch, Wear OS' },
  ];

  // Auto-select recommended functionalities after revenue model selection
  useEffect(() => {
    if (formData.revenueModels.length > 0 && formData.platforms.length > 0) {
      const recommended = getRecommendedFunctionalities(formData.revenueModels, formData.platforms);
      const newSelected: SelectedFunctionality[] = recommended.map(funcId => ({
        functionalityId: funcId,
        selectedSubChoices: [],
      }));
      
      // Merge with existing selections, keeping user's sub-choices
      setFormData(prev => {
        const existing = prev.selectedFunctionalities.filter(f => 
          !recommended.includes(f.functionalityId)
        );
        return {
          ...prev,
          selectedFunctionalities: [...existing, ...newSelected],
        };
      });
    }
  }, [formData.revenueModels, formData.platforms]);

  const simulateLoading = (duration: number = 2000) => {
    setIsLoading(true);
    return new Promise(resolve => {
      setTimeout(() => {
        setIsLoading(false);
        resolve(true);
      }, duration);
    });
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // After description, show loading then go to revenue model
      await simulateLoading(2500);
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // After revenue model, show loading then go to target users
      await simulateLoading(2000);
      setCurrentStep(4);
    } else if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const projectDescription: ProjectDescription = {
      description: formData.description,
      targetUsers: formData.targetUsers,
      techStack: formData.techStack.length > 0 ? formData.techStack : undefined,
    };

    onComplete({
      platforms: formData.platforms,
      revenueModels: formData.revenueModels,
      projectDescription,
      selectedFunctionalities: formData.selectedFunctionalities,
    });
  };

  const togglePlatform = (platform: Platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  const toggleRevenueModel = (model: RevenueModel) => {
    setFormData(prev => ({
      ...prev,
      revenueModels: prev.revenueModels.includes(model)
        ? prev.revenueModels.filter(m => m !== model)
        : [...prev.revenueModels, model],
    }));
  };

  const toggleFunctionality = (funcId: string) => {
    setFormData(prev => {
      const exists = prev.selectedFunctionalities.find(f => f.functionalityId === funcId);
      if (exists) {
        return {
          ...prev,
          selectedFunctionalities: prev.selectedFunctionalities.filter(f => f.functionalityId !== funcId),
        };
      } else {
        return {
          ...prev,
          selectedFunctionalities: [...prev.selectedFunctionalities, { functionalityId: funcId, selectedSubChoices: [] }],
        };
      }
    });
  };

  const toggleSubChoice = (funcId: string, subChoiceId: string) => {
    setFormData(prev => {
      const func = prev.selectedFunctionalities.find(f => f.functionalityId === funcId);
      if (!func) return prev;

      const updatedFunc = {
        ...func,
        selectedSubChoices: func.selectedSubChoices.includes(subChoiceId)
          ? func.selectedSubChoices.filter(id => id !== subChoiceId)
          : [...func.selectedSubChoices, subChoiceId],
      };

      return {
        ...prev,
        selectedFunctionalities: prev.selectedFunctionalities.map(f =>
          f.functionalityId === funcId ? updatedFunc : f
        ),
      };
    });
  };

  const toggleTechStack = (tech: string) => {
    setFormData(prev => ({
      ...prev,
      techStack: prev.techStack.includes(tech)
        ? prev.techStack.filter(t => t !== tech)
        : [...prev.techStack, tech],
    }));
  };

  const isFunctionalitySelected = (funcId: string) => {
    return formData.selectedFunctionalities.some(f => f.functionalityId === funcId);
  };

  const isSubChoiceSelected = (funcId: string, subChoiceId: string) => {
    const func = formData.selectedFunctionalities.find(f => f.functionalityId === funcId);
    return func?.selectedSubChoices.includes(subChoiceId) || false;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.platforms.length > 0;
      case 1:
        return formData.description.length >= 20;
      case 2:
        return formData.revenueModels.length > 0;
      case 4:
        return formData.targetUsers.length > 0;
      case 5:
        return formData.selectedFunctionalities.length > 0;
      default:
        return true;
    }
  };

  const totalSteps = 7; // platform, description, loading, revenue, loading, target users, functionalities, tech stack

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <Loader2 className="w-16 h-16 text-orcsys-500 animate-spin mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Analyse en cours...</h3>
          <p className="text-slate-600">Nous analysons vos réponses pour personnaliser votre estimation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        {/* Desktop Stepper */}
        <div className="hidden md:flex items-center justify-between mb-2">
          {[0, 1, 2, 4, 5, 6].map((stepNum, index) => {
            const stepLabels = ['Plateforme', 'Description', 'Revenus', 'Utilisateurs', 'Fonctionnalités', 'Technologies'];
            const isActive = currentStep >= stepNum;
            const isCurrent = currentStep === stepNum;
            return (
              <div key={stepNum} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      isActive
                        ? 'bg-orcsys-500 text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {isActive && !isCurrent ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <p className={`text-xs mt-2 text-center ${isActive ? 'text-slate-900' : 'text-slate-400'}`}>
                    {stepLabels[index]}
                  </p>
                </div>
                {index < stepLabels.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-2 transition-all ${
                      isActive ? 'bg-orcsys-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile Stepper */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-4">
            {[0, 1, 2, 4, 5, 6].map((stepNum, index) => {
              const isActive = currentStep >= stepNum;
              const isCurrent = currentStep === stepNum;
              return (
                <div key={stepNum} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1 relative">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all z-10 ${
                        isActive
                          ? 'bg-orcsys-500 text-white'
                          : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {isActive && !isCurrent ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        index + 1
                      )}
                    </div>
                  </div>
                  {index < 5 && (
                    <div
                      className={`h-0.5 flex-1 mx-1 transition-all ${
                        isActive ? 'bg-orcsys-500' : 'bg-slate-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {/* Current step label on mobile */}
          <div className="text-center">
            <p className="text-sm font-semibold text-slate-900">
              {(() => {
                const stepLabels = ['Plateforme', 'Description', 'Revenus', 'Utilisateurs', 'Fonctionnalités', 'Technologies'];
                const stepIndex = [0, 1, 2, 4, 5, 6].findIndex(s => s === currentStep);
                return stepIndex >= 0 ? stepLabels[stepIndex] : '';
              })()}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Étape {(() => {
                const stepIndex = [0, 1, 2, 4, 5, 6].findIndex(s => s === currentStep);
                return stepIndex >= 0 ? `${stepIndex + 1}/6` : '';
              })()}
            </p>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          {/* Step 0: Platform */}
          {currentStep === 0 && (
            <>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Quelle(s) plateforme(s) ?
              </h2>
              <p className="text-slate-600 mb-8">
                Sélectionnez les plateformes sur lesquelles votre projet sera disponible
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {platformOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = formData.platforms.includes(option.value);
                  return (
                    <motion.button
                      key={option.value}
                      type="button"
                      onClick={() => togglePlatform(option.value)}
                      className={`p-6 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? 'border-orcsys-500 bg-orcsys-50'
                          : 'border-slate-200 hover:border-orcsys-200'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center mb-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-orcsys-500 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-orcsys-500 ml-auto" />
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1">{option.label}</h3>
                      <p className="text-sm text-slate-600">{option.description}</p>
                    </motion.button>
                  );
                })}
              </div>
              {formData.platforms.length > 0 && (
                <div className="mt-6 p-4 bg-orcsys-50 rounded-lg">
                  <p className="text-sm font-medium text-orcsys-700 mb-2">
                    Plateformes sélectionnées ({formData.platforms.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.platforms.map((platform) => {
                      const option = platformOptions.find(o => o.value === platform);
                      return (
                        <span
                          key={platform}
                          className="px-3 py-1 bg-orcsys-500 text-white rounded-full text-sm font-medium"
                        >
                          {option?.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 1: Description */}
          {currentStep === 1 && (
            <>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Décrivez votre projet
              </h2>
              <p className="text-slate-600 mb-8">
                Parlez-nous de votre projet, ses objectifs et ce que vous souhaitez accomplir
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description du projet *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={8}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent"
                  placeholder="Décrivez votre projet en détail..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.description.length}/20 caractères minimum
                </p>
              </div>
            </>
          )}

          {/* Step 2: Revenue Models */}
          {currentStep === 2 && (
            <>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Modèle(s) de revenus ?
              </h2>
              <p className="text-slate-600 mb-6">
                Sélectionnez comment vous souhaitez monétiser votre projet (plusieurs choix possibles)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {revenueModelOptions.map((option) => {
                  const isSelected = formData.revenueModels.includes(option.value);
                  return (
                    <motion.button
                      key={option.value}
                      type="button"
                      onClick={() => toggleRevenueModel(option.value)}
                      className={`p-6 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? 'border-orcsys-500 bg-orcsys-50'
                          : 'border-slate-200 hover:border-orcsys-200'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center mb-3">
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-orcsys-500 mr-2" />
                        )}
                        <h3 className="font-semibold text-slate-900 flex-1">{option.label}</h3>
                      </div>
                      <p className="text-sm text-slate-600">{option.description}</p>
                    </motion.button>
                  );
                })}
              </div>
              {formData.revenueModels.length > 0 && (
                <div className="mt-6 p-4 bg-orcsys-50 rounded-lg">
                  <p className="text-sm font-medium text-orcsys-700 mb-2">
                    Modèles sélectionnés ({formData.revenueModels.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.revenueModels.map((model) => {
                      const option = revenueModelOptions.find(o => o.value === model);
                      return (
                        <span
                          key={model}
                          className="px-3 py-1 bg-orcsys-500 text-white rounded-full text-sm font-medium"
                        >
                          {option?.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 4: Target Users */}
          {currentStep === 4 && (
            <>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Utilisateurs cibles
              </h2>
              <p className="text-slate-600 mb-8">
                Qui sont vos utilisateurs cibles ?
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Utilisateurs cibles *
                </label>
                <input
                  type="text"
                  value={formData.targetUsers}
                  onChange={(e) => setFormData(prev => ({ ...prev, targetUsers: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent"
                  placeholder="Ex: Entrepreneurs, PME, Particuliers, Étudiants..."
                />
              </div>
            </>
          )}

          {/* Step 5: Functionalities */}
          {currentStep === 5 && (
            <>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Fonctionnalités clés
              </h2>
              <p className="text-slate-600 mb-8">
                Sélectionnez les fonctionnalités dont vous avez besoin. Certaines sont déjà sélectionnées selon vos choix précédents.
              </p>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {FUNCTIONALITIES.map((func) => {
                  const isSelected = isFunctionalitySelected(func.id);
                  const selectedFunc = formData.selectedFunctionalities.find(f => f.functionalityId === func.id);
                  
                  return (
                    <div
                      key={func.id}
                      className={`p-6 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-orcsys-500 bg-white'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <button
                              type="button"
                              onClick={() => toggleFunctionality(func.id)}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                isSelected
                                  ? 'border-orcsys-500 bg-orcsys-500'
                                  : 'border-slate-300 hover:border-orcsys-300'
                              }`}
                            >
                              {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                            </button>
                            <div>
                              <h3 className="font-semibold text-slate-900">{func.name}</h3>
                              <p className="text-sm text-slate-600">{func.description}</p>
                            </div>
                          </div>
                        </div>
                        {func.required && (
                          <span className="px-2 py-1 bg-orcsys-100 text-orcsys-700 rounded text-xs font-medium">
                            Recommandé
                          </span>
                        )}
                      </div>

                      {isSelected && func.subChoices && func.subChoices.length > 0 && (
                        <div className="mt-4 pt-4 border-t-2 border-slate-200">
                          <div className="flex items-center gap-2 mb-3">
                            <p className="text-sm font-semibold text-slate-900">
                              Personnalisez cette fonctionnalité :
                            </p>
                            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                              Cliquez pour sélectionner
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {func.subChoices.map((subChoice) => {
                              const isSubSelected = isSubChoiceSelected(func.id, subChoice.id);
                              return (
                                <button
                                  key={subChoice.id}
                                  type="button"
                                  onClick={() => toggleSubChoice(func.id, subChoice.id)}
                                  className={`p-4 rounded-lg border-2 text-left transition-all relative ${
                                    isSubSelected
                                      ? 'border-orcsys-500 bg-orcsys-50 shadow-sm'
                                      : 'border-slate-200 bg-white hover:border-orcsys-300 hover:bg-orcsys-50/50 hover:shadow-sm'
                                  }`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-2 flex-1">
                                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                        isSubSelected
                                          ? 'border-orcsys-500 bg-orcsys-500'
                                          : 'border-slate-300'
                                      }`}>
                                        {isSubSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                      </div>
                                      <span className={`text-sm font-medium ${
                                        isSubSelected ? 'text-slate-900' : 'text-slate-700'
                                      }`}>
                                        {subChoice.label}
                                      </span>
                                    </div>
                                    {subChoice.price !== undefined && subChoice.price > 0 && (
                                      <span className={`text-xs font-semibold ml-2 flex-shrink-0 ${
                                        isSubSelected ? 'text-orcsys-600' : 'text-slate-500'
                                      }`}>
                                        +{subChoice.price}€
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Step 6: Tech Stack */}
          {currentStep === 6 && (
            <>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                Stack technologique (optionnel)
              </h2>
              <p className="text-slate-600 mb-8">
                Avez-vous des préférences technologiques ? Sinon, nous vous proposerons les meilleures options.
              </p>
              <div className="flex flex-wrap gap-3">
                {techStackOptions.map((tech) => {
                  const isSelected = formData.techStack.includes(tech);
                  return (
                    <button
                      key={tech}
                      type="button"
                      onClick={() => toggleTechStack(tech)}
                      className={`px-4 py-2 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-orcsys-500 bg-orcsys-50 text-orcsys-700'
                          : 'border-slate-200 hover:border-orcsys-200'
                      }`}
                    >
                      {tech}
                    </button>
                  );
                })}
              </div>
              {formData.techStack.length > 0 && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Technologies sélectionnées ({formData.techStack.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.techStack.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 bg-slate-200 text-slate-700 rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex items-center bg-orcsys-500 hover:bg-orcsys-600"
            >
              {currentStep === 6 ? 'Voir l\'estimation' : 'Suivant'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default QuestionnaireSteps;
